import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Bell, Loader2, Check, Bus, Calendar, X } from "lucide-react";

/**
 * NotificationBell — floating in-app inbox for driver assignment updates.
 * Renders a bell with an unread badge in the header, and a slide-down panel
 * listing the latest notifications. Real-time push via WebSocket
 * ("driver:{id}" room → "notification.created" event) augments the polling
 * fallback (every 60s).
 */
export default function NotificationBell({ testId = "driver-notif-bell" }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const rooms = useMemo(() => (user?.id ? [`driver:${user.id}`] : []), [user?.id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/driver/notifications?limit=30");
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch (e) {
      // Silent fail — notifications are non-critical
      console.warn("notifications load failed:", e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, [load]);

  // Real-time push from the realtime hub: when the server broadcasts a
  // `notification.created` event to the driver's own room, refresh and toast.
  useRealtime(rooms, {
    "notification.created": (evt) => {
      const p = evt?.payload || {};
      toast(p.title || "Nouvelle notification", {
        description: p.body || "",
        duration: 6000,
      });
      load();
    },
  });

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.post(`/driver/notifications/${id}/read`);
      setItems((arr) => arr.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };
  const markAllRead = async () => {
    try {
      await api.post(`/driver/notifications/mark-all-read`);
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        aria-label="Notifications"
        className="relative inline-flex items-center justify-center w-9 h-9 border border-zinc-700 text-zinc-200 hover:bg-zinc-800">
        <Bell size={16} />
        {unread > 0 && (
          <span
            data-testid={`${testId}-badge`}
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center bg-[#FFC60A] text-zinc-900 text-[10px] font-mono font-bold rounded-full border border-zinc-900">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid={`${testId}-panel`}
          className="absolute right-0 top-full mt-2 w-[330px] sm:w-[380px] max-h-[480px] bg-white text-zinc-900 border-2 border-zinc-900 shadow-[6px_6px_0_0_#FFC60A] flex flex-col z-50">
          <header className="px-3 py-2 bg-zinc-900 text-white flex items-center gap-2 flex-shrink-0">
            <Bell size={14} className="text-[#FFC60A]" />
            <div className="flex-1">
              <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-[#FFC60A]">Inbox</div>
              <div className="font-display font-black text-base tracking-tighter leading-none mt-0.5">Notifications</div>
            </div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                data-testid={`${testId}-mark-all`}
                className="px-2 py-1 text-[9px] font-mono uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:bg-zinc-800 inline-flex items-center gap-1">
                <Check size={10} /> Tout lu
              </button>
            )}
            <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white"><X size={14} /></button>
          </header>
          <div className="flex-1 overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="px-3 py-6 text-center text-zinc-400 text-sm"><Loader2 size={14} className="inline animate-spin mr-1" /> Chargement…</div>
            ) : items.length === 0 ? (
              <div className="px-3 py-8 text-center text-zinc-400 text-sm">
                <Bell size={20} className="mx-auto mb-2 text-zinc-300" />
                Aucune notification
              </div>
            ) : (
              <ul className="divide-y divide-zinc-100" data-testid={`${testId}-list`}>
                {items.map((n) => (
                  <li
                    key={n.id}
                    onClick={() => !n.read && markRead(n.id)}
                    data-testid={`${testId}-item-${n.id}`}
                    className={`px-3 py-2 cursor-pointer hover:bg-zinc-50 ${!n.read ? "bg-yellow-50/60 border-l-4 border-l-[#FFC60A]" : ""}`}>
                    <div className="flex items-start gap-2">
                      <Bus size={13} className="text-zinc-500 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{n.title}</div>
                        <div className="text-xs text-zinc-600">{n.body}</div>
                        <div className="text-[10px] font-mono text-zinc-400 mt-1 inline-flex items-center gap-1">
                          <Calendar size={9} />
                          {new Date(n.created_at).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}
                        </div>
                      </div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-[#FFC60A] shrink-0 mt-1" />}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
